/**
 * Schema-Driven Vessel Configuration Mapper
 * Automatically maps ALL fields from vessel schema
 * No more manual whitelisting!
 */

import { getAllVesselFields, LEGACY_FIELD_MAPPINGS, COMPUTED_FIELDS } from '../../../../config/vessel.schema';

/**
 * Maps vessel data from storage to form configuration
 * SCHEMA-DRIVEN: Automatically handles ALL fields from vessel schema
 * @param {Object} vesselFromContext - Vessel data from VesselContext
 * @returns {Object} Configuration object for form
 */
export const mapVesselToConfig = (vesselFromContext) => {
  if (!vesselFromContext) return null;
  
  const config = {};
  
  // Get all vessel fields from schema
  const allFields = getAllVesselFields();
  
  // Map each field automatically
  allFields.forEach(field => {
    // Skip computed fields (they're derived from other fields)
    if (COMPUTED_FIELDS.includes(field)) {
      return;
    }
    
    // Get the value from vessel (check for legacy field names too)
    let value = vesselFromContext[field];
    
    // Check legacy mappings if value is undefined
    if (value === undefined) {
      const legacyKey = Object.keys(LEGACY_FIELD_MAPPINGS).find(
        key => LEGACY_FIELD_MAPPINGS[key] === field
      );
      if (legacyKey && vesselFromContext[legacyKey] !== undefined) {
        value = vesselFromContext[legacyKey];
      }
    }
    
    // Special handling for specific fields
    if (field === 'category' && value !== undefined) {
      // STRIP 'cat' prefix for form display - dropdown options are '0', '1', '2', etc.
      if (typeof value === 'string' && value.startsWith('cat')) {
        value = value.replace('cat', '');
      } else if (typeof value === 'number') {
        value = String(value);
      }
      // Form expects plain number strings like '3', not 'cat3'
    } else if (field === 'name' && !value) {
      // Map from vesselName if name is missing
      value = vesselFromContext.vesselName || '';
    } else if (field === 'type' && !value) {
      // Map from vesselType if type is missing
      value = vesselFromContext.vesselType || 'support';
    } else if (field === 'openFlameProtection') {
      // Handle nested openFlameProtection object
      value = vesselFromContext.openFlameProtection || {
        blanket: false,
        extraExtinguisher: false,
        fixedSystem: false
      };
    } else if (field === 'fixedFFE') {
      // Handle nested fixedFFE object (space-specific fixed systems)
      value = vesselFromContext.fixedFFE || {
        engine: false,
        galley: false,
        accommodation: false
      };
    } else if (field === 'fixedSystems') {
      // Legacy field - keep for backward compatibility
      value = vesselFromContext.fixedSystems || {
        machinery: false,
        accommodation: false,
        paintLocker: false,
        other: false
      };
    } else if (field === 'engineBoxProtection') {
      // Handle nested engineBoxProtection object
      value = vesselFromContext.engineBoxProtection || {
        firePort: false,
        fixedSystem: false
      };
    }
    
    // Set the field in config (use default if undefined)
    config[field] = value !== undefined ? value : getDefaultValue(field);
  });
  
  // Handle form-specific field names (vesselName, vesselType for backwards compatibility)
  // BOTH directions: canonical -> form field AND form field -> canonical
  config.vesselName = config.name || vesselFromContext.vesselName || '';
  config.vesselType = config.type || vesselFromContext.vesselType || 'support';
  
  // Also ensure canonical fields are populated from form fields if missing
  if (!config.name && config.vesselName) {
    config.name = config.vesselName;
  }
  if (!config.type && config.vesselType) {
    config.type = config.vesselType;
  }
  
  // Ensure equipment settings have defaults
  if (!config.equipmentCheckSettings) {
    config.equipmentCheckSettings = {
      lsaFrequency: 30,
      ffeFrequency: 30
    };
  }
  
  // Ensure certificates structure exists
  if (!config.certificates) {
    config.certificates = {
      mlc: { number: '', expiry: '' },
      passengerShipSafety: { number: '', expiry: '' },
      cargoShipSafety: { number: '', expiry: '' },
      workboatCode: { number: '', expiry: '' },
      smallVesselCert: { number: '', expiry: '' },
      loadLine: { number: '', expiry: '' },
      safetyConstruction: { number: '', expiry: '' },
      safetyEquipment: { number: '', expiry: '' },
      safetyRadio: { number: '', expiry: '' },
      iopp: { number: '', expiry: '' },
      insurance: { number: '', expiry: '' }
    };
  }
  
  // LAZY MIGRATION: Recalculate galley-related derived flags if undefined
  // This handles vessels created before these flags were added to the schema
  if (config.hasCooking === undefined || config.hasOpenFlame === undefined) {
    const src = config.galleyHeatSource || 'electric';
    const needsProtection = ['gas', 'open_flame', 'liquid_fuel'].includes(src);

    config.hasCooking = needsProtection || (config.hasGalley && src !== 'electric');
    config.hasOpenFlame = needsProtection;
    config.hasHeating = config.hasHeating || false;
    config.hasGasAppliance = src === 'gas';
    config.hasLiquidFuelAppliance = src === 'liquid_fuel';
  }

  // MAPPER-SIDE ENFORCEMENT: Keep galleyHeatSource in sync with derived flags
  // This ensures consistency even if data gets out of sync
  const src = config.galleyHeatSource || 'electric';
  if (src === 'gas') {
    config.hasOpenFlame = true;
    config.hasGasAppliance = true;
    config.hasLiquidFuelAppliance = false;
  } else if (src === 'liquid_fuel') {
    config.hasOpenFlame = true;
    config.hasGasAppliance = false;
    config.hasLiquidFuelAppliance = true;
  } else if (src === 'electric') {
    config.hasOpenFlame = false;
    config.hasGasAppliance = false;
    config.hasLiquidFuelAppliance = false;
  }

  // Log unmapped fields in development
  if (process.env.NODE_ENV === 'development') {
    const vesselKeys = Object.keys(vesselFromContext);
    const unmappedKeys = vesselKeys.filter(key =>
      !allFields.includes(key) &&
      !Object.keys(LEGACY_FIELD_MAPPINGS).includes(key) &&
      key !== 'id' && // Allow id to pass through
      key !== 'createdAt' && // Allow timestamps
      key !== 'updatedAt'
    );

    if (unmappedKeys.length > 0) {

    }
  }

  return config;
};

/**
 * Maps form configuration to vessel storage format
 * SCHEMA-DRIVEN: Automatically handles ALL fields from vessel schema
 * @param {Object} vesselConfig - Configuration from form
 * @returns {Object} Vessel data for storage
 */
export const mapConfigToVessel = (vesselConfig) => {
  if (!vesselConfig) return null;
  
  const vessel = {};
  
  // Get all vessel fields from schema
  const allFields = getAllVesselFields();
  
  // Map each field automatically
  allFields.forEach(field => {
    // Skip computed fields initially
    if (COMPUTED_FIELDS.includes(field)) {
      return;
    }
    
    // Get the value from config
    let value = vesselConfig[field];
    
    // Check for form-specific field names - PRIORITIZE FORM FIELDS
    if (field === 'name') {
      // Prioritize vesselName (form field) over name (canonical)
      value = vesselConfig.vesselName || vesselConfig.name;
    } else if (field === 'type') {
      // Prioritize vesselType (form field) over type (canonical)
      value = vesselConfig.vesselType || vesselConfig.type;
    }
    
    // Special handling for category
    if (field === 'category' && value !== undefined) {
      // PRESERVE 'cat' prefix format - this is the canonical format
      // flattenVesselConfig uses parseCategory() which handles both formats
      if (typeof value === 'number') {
        value = `cat${value}`; // Convert number to 'catX' format
      } else if (typeof value === 'string' && !value.startsWith('cat')) {
        value = `cat${value}`; // Add 'cat' prefix if missing
      }
      // Keep 'cat' prefix for consistent storage
    }
    
    // Special handling for openFlameProtection - it's an object
    if (field === 'openFlameProtection') {
      // Already an object, pass through
      value = vesselConfig.openFlameProtection || {
        blanket: false,
        extraExtinguisher: false,
        fixedSystem: false
      };
    }
    
    // Special handling for engineBoxProtection - it's an object
    if (field === 'engineBoxProtection') {
      // Already an object, pass through
      value = vesselConfig.engineBoxProtection || {
        firePort: false,
        fixedSystem: false
      };
    }
    
    // Set the field in vessel if it has a value
    if (value !== undefined) {
      vessel[field] = value;
    }
  });
  
  // Add legacy field mappings for backward compatibility
  if (vessel.name) {
    vessel.vesselName = vessel.name;
  }
  if (vessel.type) {
    vessel.vesselType = vessel.type;
  }
  if (vessel.category !== undefined) {
    vessel.areaCategory = vessel.category; // Alternative name used in maintenance files
  }
  if (vessel.port) {
    vessel.homePort = vessel.port;
  }
  if (vessel.crewCount && !vessel.crewCapacity) {
    vessel.crewCapacity = vessel.crewCount;
  }
  
  // Handle computed equipment flags based on optionalEquipment
  if (vesselConfig.optionalEquipment && Array.isArray(vesselConfig.optionalEquipment)) {
    vessel.hasRadar = vesselConfig.optionalEquipment.includes('radar') || undefined;
    vessel.hasNavtex = vesselConfig.optionalEquipment.includes('navtex') || undefined;
    vessel.hasMF = vesselConfig.optionalEquipment.includes('mf_radio') || undefined;
    vessel.hasAIS = vesselConfig.optionalEquipment.includes('ais') || undefined;
    vessel.hasLiferaft = vesselConfig.optionalEquipment.includes('liferaft') || undefined;
  }
  
  // Ensure critical fields exist
  if (!vessel.id && vesselConfig.id) {
    vessel.id = vesselConfig.id;
  }
  
  // Log fields that might be lost in development
  if (process.env.NODE_ENV === 'development') {
    const configKeys = Object.keys(vesselConfig);
    const unmappedKeys = configKeys.filter(key => 
      !allFields.includes(key) && 
      key !== 'vesselName' && // Form-specific field
      key !== 'vesselType' && // Form-specific field
      !vessel[key] // Not already mapped
    );
    
    if (unmappedKeys.length > 0) {

    }
  }
  
  return vessel;
};

/**
 * Get default value for a field based on its type
 * @param {string} field - Field name
 * @returns {any} Default value
 */
export function getDefaultValue(field) {
  // Boolean fields default to false
  const booleanFields = [
    'internationalVoyages', 'towing_capable', 'engineer_onboard',
    'hasAccommodation', 'hasGalley', 'hasWheelhouse', 'hasMachinerySpace',
    'hasEnclosedSpaces', 'enclosed_accommodation_present', 'cabin_accommodation',
    'hasGasAppliances', 'hasLPGSystem', 'hasLithiumBatteries', 'hasConcentratedElectrical',
    'hasExhaustLeakRisk', 'hasGasLeakRisk', 'hasFireRiskArea', 'gasAppliances',
    'hasEmergencySteering', 'power_critical_systems', 'bilge_auto', 'hasShorepower',
    'isPracticable', 'deemedNecessary', 'companyRequiresBuckets',
    'hasProhibitedDischargeArea', 'hasIMOSewageSystem', 'isUnmanned',
    'liferafts_fitted', 'hru_fitted', 'lifeboat_fitted', 'rescue_boat_fitted',
    'carriesVulnerablePassengers', 'operatesAtNight', 'allPersonsWearLifejackets',
    'hasDecking', 'hasHeating', 'hasOilLubricatedShaft', 'hasWorkboatPilotEndorsement',
    // Galley-related derived flags (added for schema fix)
    'hasCooking', 'hasOpenFlame', 'hasGasAppliance', 'hasLiquidFuelAppliance'
  ];
  
  // Array fields default to empty array
  const arrayFields = [
    'equipment', 'operations', 'optionalEquipment', 'specialEquipment', 'companyRequiredDrills'
  ];
  
  // Number fields default to empty string (for form inputs)
  const numberFields = [
    'grossTonnage', 'lengthOverall', 'draft', 'enginePower', 'age',
    'numberOfEngines', 'maxPersons', 'passengerCapacity', 'crewCount',
    'crewCapacity', 'oilCapacityLitres', 'liferaft_capacity'
  ];
  
  // String fields with specific defaults
  const stringDefaults = {
    'flag': 'GB',
    'engineType': 'diesel',
    'enginePosition': 'below_deck',
    'propulsionType': 'shaft',
    'propulsionConfiguration': '',
    'driveType': '',
    'steeringType': 'hydraulic',
    'operatingStatus': 'seagoing',
    'operatingHours': 'under_24h',
    'galleyHeatSource': 'electric',
    'category': '3',
    'type': 'support'
  };
  
  // Object fields with specific defaults
  const objectDefaults = {
    'openFlameProtection': {
      blanket: false,
      extraExtinguisher: false,
      fixedSystem: false
    },
    'engineBoxProtection': {
      firePort: false,
      fixedSystem: false
    }
  };
  
  if (booleanFields.includes(field)) {
    return false;
  } else if (arrayFields.includes(field)) {
    return [];
  } else if (numberFields.includes(field)) {
    return '';
  } else if (stringDefaults[field]) {
    return stringDefaults[field];
  } else if (objectDefaults[field]) {
    return objectDefaults[field];
  } else {
    return '';
  }
}

/**
 * FT-515 round 9: a "default-valued vessel" — every schema field at exactly
 * the value a vessel that has NEVER had that field set would carry after
 * going through a real save. Used by VesselSettingsView's audit diff to tell
 * "this field was never set before" (so landing on its default isn't a real
 * edit) apart from "this field was genuinely changed" (see
 * computeVesselSettingsDiff for the actual comparison).
 *
 * Deliberately NOT a second hand-written table of "what counts as empty per
 * field type" — `getDefaultValue` already is that table, but its answer
 * alone isn't enough: a few fields (`category` is the clearest example) are
 * re-shaped by mapConfigToVessel on the way to storage (form-shape default
 * '3' becomes storage-shape 'cat3'), and re-deriving which fields get
 * reshaped and how would just be a parallel copy of logic that already lives
 * in mapConfigToVessel. Running the REAL mapVesselToConfig -> mapConfigToVessel
 * round trip on an empty vessel produces the actual storage-shape default for
 * every field, through the exact same code a real save uses, with no
 * field-by-field guessing.
 *
 * Pure/static for the lifetime of the app (depends only on the schema and
 * the mapper's own defaulting logic), so computed once and memoized.
 */
let _defaultVesselSnapshot = null;
export function getDefaultVesselSnapshot() {
  if (!_defaultVesselSnapshot) {
    _defaultVesselSnapshot = mapConfigToVessel(mapVesselToConfig({}));
  }
  return _defaultVesselSnapshot;
}

/**
 * Validate that mapper handles all schema fields
 * @returns {Object} Validation result with missing fields
 */
export function validateMapper() {
  const allFields = getAllVesselFields();
  const testVessel = {};
  
  // Create a test vessel with all fields
  allFields.forEach(field => {
    testVessel[field] = `test_${field}`;
  });
  
  // Map to config and back
  const config = mapVesselToConfig(testVessel);
  const result = mapConfigToVessel(config);
  
  // Check which fields were lost
  const missingFields = allFields.filter(field => {
    // Skip computed fields as they're derived
    if (COMPUTED_FIELDS.includes(field)) {
      return false;
    }
    // Check if field exists in result
    return !result[field] && !result[LEGACY_FIELD_MAPPINGS[field]];
  });
  
  return {
    valid: missingFields.length === 0,
    missingFields,
    totalFields: allFields.length,
    mappedFields: allFields.length - missingFields.length
  };
}